import dynamic from 'next/dynamic'
import React, { useCallback, useState } from 'react'
import { IoChevronDownSharp } from 'react-icons/io5'
import styled, { keyframes } from 'styled-components'
import { Scalars, User } from 'types/graphql'
import { media } from 'utils/media'
import { Button } from 'lib/Button/Button'
import { MaxWidthContainer } from 'lib/Section'
import { useCurrentUser } from 'modules/auth/useCurrentUser'
import { RatableBrandPlaceholder } from 'modules/brands/list/components/RatableBrand'
import { useUnreviewedBrands } from 'modules/brands/queries/useUnReviewedBrands'
import { SectionHeader } from 'modules/homepage/SectionHeader'

const BRAND_CHUNK_SIZE_DESKTOP = 6
const BRAND_CHUNK_SIZE_REST = 4

const RatableBrand = dynamic(() => import('modules/brands/list/components/RatableBrand').then((res) => res.RatableBrand), { ssr: false })
const UserPreviewLink = dynamic(() => import('modules/community/UserPreviewLink').then((res) => res.UserPreviewLink))
const SupertesterProgress = dynamic(() => import('modules/supertester/components/SupertesterProgress').then((res) => res.SupertesterProgress))

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`

const UserRowContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--wide-grid-spacing);

  ${media.phoneDown} {
    flex-direction: column;
    align-items: flex-start;
  }
`

const Container = styled(MaxWidthContainer)`
  // This has to be a grid instead of flex box to center align the load more button without a bunch of width: 100%/flex-grow stuffs
  display: grid;
  grid-template-columns: 1fr;
  padding: 0 var(--wide-grid-spacing);
  gap: var(--wide-grid-spacing);
  justify-content: center;
`

const RateableBrandWrapper = styled.li`
  animation: ${fadeIn} 0.3s ease-out;
  padding: var(--wide-grid-spacing);
  border: 1px solid var(--black-20);

  ${media.tabletDown} {
    border: none;
    border-bottom: 1px solid var(--black-20);
    padding: var(--wide-grid-spacing) 0;

    :first-child {
      border-top: 1px solid var(--black-20);
    }
  }
`

const RateableBrandsGrid = styled.ul<{ $pages: number }>`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: var(--wide-grid-spacing);

  ${media.tabletDown} {
    grid-template-columns: 1fr;
    grid-gap: 0;
  }

  ${media.smallLaptopUp} {
    grid-template-columns: repeat(3, 1fr);
    ${RateableBrandWrapper}:nth-child(n + ${(props) => props.$pages * BRAND_CHUNK_SIZE_DESKTOP + 1}) {
      display: none;
    }
  }

  ${media.smallLaptopDown} {
    ${RateableBrandWrapper}:nth-child(n + ${(props) => props.$pages * BRAND_CHUNK_SIZE_REST + 1}) {
      display: none;
    }
  }

  }
`

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-self: center;
  svg {
    // Offset to align with lower case text in button
    margin-top: 4px;
  }
`

const ChevronDownIcon = styled(IoChevronDownSharp)`
  margin-top: 0;
  position: relative;
  top: -1px;
`

const UserRow = ({ user }: { user: Pick<User, 'fullName' | 'username' | 'avatarFullPath' | 'reviewCount' | 'isSupertester'> }) => (
  <UserRowContainer>
    <UserPreviewLink
      name={user.fullName ?? user.username}
      username={user.username}
      reviewCount={user.reviewCount}
      avatarFullPath={user.avatarFullPath}
      showPopoverOnHover={false}
    />
    {!user.isSupertester && <SupertesterProgress />}
  </UserRowContainer>
)

export const WriteReviewSection = () => {
  const { user } = useCurrentUser()
  const { loading, unreviewedBrands, called, refetchDiscovery } = useUnreviewedBrands({ amountToReturn: 30, allowCache: true, addRandomness: true })
  const [pagesShown, setPagesShown] = useState(1)
  const [dismissedBrandIds, setDismissedBrandIds] = useState<Scalars['ID'][]>([])

  const fakeFetchMore = useCallback(() => {
    setPagesShown((x) => x + 1)
    window.analytics.track('Homepage Write Review Section Load More Clicked')
  }, [])

  const onBrandDismissed = (brandId: Scalars['ID']) => () => {
    refetchDiscovery()
    setDismissedBrandIds((prev) => [...prev, brandId])
  }

  return (
    <Container as="section">
      <SectionHeader title={user?.firstName ? `${user.firstName}, write your next review` : 'Write a review'} />

      {user && <UserRow user={user} />}

      <RateableBrandsGrid $pages={pagesShown}>
        {unreviewedBrands
          ?.filter((brand) => !dismissedBrandIds.includes(brand.id))
          .map((brand) => (
            <RateableBrandWrapper key={brand.id}>
              <RatableBrand brand={brand} linkToBrandPage onDismissClick={onBrandDismissed(brand.id)} />
            </RateableBrandWrapper>
          ))}

        {(!called || loading) &&
          unreviewedBrands?.length === 0 &&
          Array.from({ length: 6 }).map((_, ix) => (
            <RateableBrandWrapper key={`placeholder-${ix}`}>
              <RatableBrandPlaceholder />
            </RateableBrandWrapper>
          ))}
      </RateableBrandsGrid>

      {pagesShown < 5 && (
        <StyledButton onClick={fakeFetchMore} disabled={loading} label="More suggestions" endIcon={<ChevronDownIcon />} variant="ghost" />
      )}
    </Container>
  )
}
